import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SectionCard from "@/components/customers/SectionCard";
import { useAuth } from "@/contexts/AuthContext";
import { useProjects } from "@/hooks/use-projects";
import { formatDate } from "@/lib/format";
import { ratingSummary } from "@/lib/freelancers";
import {
  useDeleteFreelancerReview,
  useFreelancerReviews,
  useSaveFreelancerReview,
  type FreelancerReviewRow,
} from "@/hooks/use-freelancer-reviews";
import { RatingStars } from "./RatingStars";

const NO_PROJECT = "none";

function ReviewDialog({ open, onOpenChange, freelancerId, freelancerName, review }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  freelancerId: string;
  freelancerName: string;
  review: FreelancerReviewRow | null;
}) {
  // Startverdier fra props; forelderen gir dialogen ny key ved hver åpning.
  const [rating, setRating] = useState(review?.rating ?? 0);
  const [comment, setComment] = useState(review?.comment ?? "");
  const [projectId, setProjectId] = useState<string>(review?.project_id ?? NO_PROJECT);
  const { data: projects = [] } = useProjects();
  const save = useSaveFreelancerReview();

  const submit = () =>
    save.mutate(
      { id: review?.id, input: { freelancer_id: freelancerId, rating, comment, project_id: projectId === NO_PROJECT ? null : projectId } },
      { onSuccess: () => onOpenChange(false) },
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Vurder {freelancerName}</DialogTitle>
          <DialogDescription>Skriv saklig. Frilanseren kan be om innsyn i det som står her.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Vurdering *</Label>
            <RatingStars value={rating || null} onChange={setRating} size="lg" />
          </div>
          <div className="space-y-1.5">
            <Label>Prosjekt</Label>
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger aria-label="Prosjekt"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={NO_PROJECT}>Ikke knyttet til prosjekt</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{[p.project_code, p.name].filter(Boolean).join(" ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="review-comment">Kommentar</Label>
            <Textarea id="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Hva gikk bra, hva bør neste PL vite?" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={submit} disabled={rating < 1 || save.isPending}>Lagre vurdering</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function FreelancerReviewsSection({ freelancerId, freelancerName }: { freelancerId: string; freelancerName: string }) {
  const { user, isAdmin } = useAuth();
  const { data: reviews = [] } = useFreelancerReviews(freelancerId);
  const remove = useDeleteFreelancerReview();
  const [editing, setEditing] = useState<FreelancerReviewRow | null>(null);
  const [open, setOpen] = useState(false);
  const [openCount, setOpenCount] = useState(0);
  const { avg, count } = ratingSummary(reviews.map((r) => r.rating));

  const openDialog = (review: FreelancerReviewRow | null) => {
    setEditing(review);
    setOpenCount((n) => n + 1);
    setOpen(true);
  };

  return (
    <>
      <SectionCard
        title="Vurderinger"
        compact
        meta={count > 0 ? `${avg?.toLocaleString("nb-NO")} · ${count}` : undefined}
        empty="Ingen vurderinger ennå."
        actions={<Button variant="outline" size="sm" onClick={() => openDialog(null)}>Gi vurdering</Button>}
      >
        {reviews.length > 0 && (
          <ul className="divide-y divide-border">
            {reviews.map((r) => {
              const mine = r.created_by === user?.id;
              return (
                <li key={r.id} className="space-y-1 px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <RatingStars value={r.rating} />
                    <span>{r.source === "monday_import" ? "Importert fra Monday" : r.author?.full_name ?? "Ukjent"}</span>
                    {/* Importdatoen fra Monday er ikke når vurderingen ble gitt, så den vises ikke. */}
                    {r.source !== "monday_import" && <span>· {formatDate(r.created_at)}</span>}
                    {r.project && <span>· {[r.project.project_code, r.project.name].filter(Boolean).join(" ")}</span>}
                  </div>
                  {r.comment && <p className="text-sm">{r.comment}</p>}
                  {(mine || isAdmin) && (
                    <div className="flex gap-3 text-xs">
                      <button type="button" className="text-primary hover:underline" onClick={() => openDialog(r)}>Endre</button>
                      <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => remove.mutate({ id: r.id, freelancerId })}>Slett</button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>
      <ReviewDialog key={openCount} open={open} onOpenChange={setOpen} freelancerId={freelancerId} freelancerName={freelancerName} review={editing} />
    </>
  );
}
